import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Alert, Button, Field, Input } from '../components/ui'
import { api } from '../utils/api'
import { toErrorMessage } from '../lib/errors'
import Logo from '../components/ui/Logo'

/**
 * Dove chi ha dimenticato la password chiede il link.
 *
 * La risposta è sempre la stessa: che l'indirizzo abbia un account o no, la
 * pagina dice che se c'è un account l'email sta arrivando. Non può fare
 * altrimenti senza diventare un modo per scoprire chi ha un account.
 */
export default function ForgotPassword() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    setError(null)

    try {
      await api.post('/api/password-reset/request', { email: email.trim() })
      setSent(true)
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 dark:bg-gray-950">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Logo />
        </div>

        <div className="rounded-lg bg-white p-8 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
            {t('passwordReset.forgotTitle')}
          </h1>

          {sent ? (
            <>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400" role="status">
                {t('passwordReset.sent')}
              </p>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {t('passwordReset.sentHint')}
              </p>
            </>
          ) : (
            <>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {t('passwordReset.forgotDescription')}
              </p>

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <Field label={t('auth.email')} required>
                  <Input
                    type="email"
                    name="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </Field>

                {error && <Alert>{error}</Alert>}

                <Button type="submit" className="w-full" loading={submitting}>
                  {t('passwordReset.sendLink')}
                </Button>
              </form>
            </>
          )}

          <Link
            to="/login"
            className="mt-6 inline-block text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            {t('passwordReset.backToLogin')}
          </Link>
        </div>
      </div>
    </div>
  )
}
